'use client';

import Link from 'next/link';
import { X, MessageCircle } from 'lucide-react';
import type { VerseReference } from '@/lib/types';
import CrossReferences from './CrossReferences';

interface VerseHighlightProps {
  verse: VerseReference;
  book: string;
  chapter: number;
  onClose: () => void;
}

export default function VerseHighlight({
  verse,
  book,
  chapter,
  onClose,
}: VerseHighlightProps) {
  return (
    <div
      className="sticky top-6 p-4 rounded-xl"
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid rgba(200, 164, 94, 0.15)',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <h3
          className="text-base font-semibold"
          style={{ color: 'var(--accent-gold)' }}
        >
          {book} {chapter}:{verse.verse}
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded transition-colors hover:bg-white/10"
          style={{ color: 'var(--text-secondary)' }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Full text */}
      <p
        className="text-sm leading-relaxed mb-4"
        style={{ color: 'var(--text-primary)' }}
      >
        {verse.text}
      </p>

      {/* Translation badge */}
      {verse.translation && (
        <div className="mb-4">
          <span
            className="text-xs px-2 py-0.5 rounded"
            style={{
              backgroundColor: 'rgba(74, 125, 189, 0.15)',
              color: 'var(--accent-blue)',
            }}
          >
            {verse.translation.toUpperCase()}
          </span>
        </div>
      )}

      <CrossReferences
        book={book}
        chapter={chapter}
        verse={verse.verse}
      />

      {/* Ask about this verse */}
      <Link
        href={`/chat?q=${encodeURIComponent(`What does ${book} ${chapter}:${verse.verse} mean?`)}`}
        className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-colors w-full justify-center"
        style={{
          backgroundColor: 'rgba(200, 164, 94, 0.1)',
          color: 'var(--accent-gold)',
          border: '1px solid rgba(200, 164, 94, 0.2)',
        }}
      >
        <MessageCircle size={14} />
        Ask about this verse
      </Link>
    </div>
  );
}
